import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  UncontrolledAccordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Button,
  Popover,
  PopoverHeader,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import {
  fetchJobPortalJobList,
  resetJobPortalJobList,
  setFilterValues,
  setPageNumber,
  fetchJobPortalWorkLocations,
} from "../../../store/job/action";

export default function FilterButton() {
  const dispatch = useDispatch();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [filterTemp, setFilterTemp] = useState({
    workModes: [],
    locations: [],
    jobTypes: [],
  });

  // Get global state variables from Redux store
  const workLocations = useSelector((state) => state.JobPortalJobReducer.workLocations);
  const filter = useSelector((state) => state.JobPortalJobReducer.filter);

  // Show only 3 locations initially
  const locationsToShow = showAll ? workLocations : workLocations.slice(0, 3);


  // Set initial value for filterTemp
  useEffect(() => {
    setFilterTemp(filter);
  }, [filter]);

  // Fetch work locations from the server
  useEffect(() => {
    if(workLocations.length === 0) {
      dispatch(fetchJobPortalWorkLocations());
    }
  }, []);

  
  // Handle workModes filter change
  function handleWorkModeChange(e) {
    const { name } = e.target;
    setFilterTemp((prev) => ({
      ...prev,
      workModes: prev.workModes.includes(name)
        ? prev.workModes.filter((mode) => mode !== name)
        : [...prev.workModes, name],
    }));
  }

  // Handle location filter change
  function handleLocationChange(e) {
    const { name } = e.target;
    setFilterTemp((prev) => ({
      ...prev,
      locations: prev.locations.includes(name)
        ? prev.locations.filter((location) => location !== name)
        : [...prev.locations, name],
    }));
  }

  // Handle jobTypes filter change
  function handleJobTypesChange(e) {
    const { name } = e.target;
    setFilterTemp((prev) => ({
      ...prev,
      jobTypes: prev.jobTypes.includes(name)
        ? prev.jobTypes.filter((type) => type !== name)
        : [...prev.jobTypes, name],
    }));
  }

  // Handle filter clear
  function handleFilterClear() {
    setFilterTemp({
      workModes: [],
      locations: [],
      jobTypes: [],
    });
  }

  // Handle filter submit
  function handleFilterSubmit(e) {
    e.preventDefault();
    e.stopPropagation();
    dispatch(setFilterValues(filterTemp));
    dispatch(setPageNumber(0))
    .then(() => dispatch(resetJobPortalJobList()))
    .then(() => dispatch(fetchJobPortalJobList(0)))
    .then(() => dispatch(fetchJobPortalWorkLocations()));
  }


  return (
    <>
      <Button
        id="filter-popover"
        className="flex-center me-2 text-nowrap bg-light border-light"
        type="button"
      >
        <i
          className="las la-sliders-h me-1 rotate-270"
          style={{ fontSize: "1.25rem" }}
        />
        Filters
      </Button>
      <Popover
        placement="bottom"
        target="filter-popover"
        hideArrow={true}
        isOpen={popoverOpen}
        toggle={() => setPopoverOpen(!popoverOpen)}
        className="popover-container"
      >
        <Form onSubmit={handleFilterSubmit}>
          <PopoverHeader className="d-flex align-items-center justify-content-between p-3">
            <span className="fs-16">Filter</span>
            <div className="ms-5">
              <Button className="me-2 clear-btn" onClick={handleFilterClear}>
                Clear
              </Button>
              <Button className="apply-btn" type="submit">
                Apply
              </Button>
            </div>
          </PopoverHeader>
          <UncontrolledAccordion flush stayOpen>
            <AccordionItem>
              <AccordionHeader targetId="1">WORK MODE</AccordionHeader>
              <AccordionBody accordionId="1">
                <FormGroup>
                  <Input
                    id="remote"
                    className="me-3"
                    name="Remote"
                    type="checkbox"
                    checked={filterTemp.workModes.includes("Remote")}
                    onChange={handleWorkModeChange}
                  />
                  <Label for="remote">Remote</Label>
                </FormGroup>
                <FormGroup>
                  <Input
                    id="hybrid"
                    className="me-3"
                    name="Hybrid "
                    type="checkbox"
                    checked={filterTemp.workModes.includes("Hybrid ")}
                    onChange={handleWorkModeChange}
                  />
                  <Label for="hybrid">Hybrid</Label>
                </FormGroup>
                <FormGroup>
                  <Input
                    id="on-site"
                    className="me-3"
                    name="On site"
                    type="checkbox"
                    checked={filterTemp.workModes.includes("On site")}
                    onChange={handleWorkModeChange}
                  />
                  <Label for="on-site" className="mb-0">On site</Label>
                </FormGroup>
              </AccordionBody>
            </AccordionItem>
            <AccordionItem>
              <AccordionHeader targetId="2">WORK TYPE</AccordionHeader>
              <AccordionBody accordionId="2">
                <FormGroup>
                  <Input
                    id="Permanent"
                    className="me-3"
                    name="Permanent"
                    type="checkbox"
                    checked={filterTemp.jobTypes.includes("Permanent")}
                    onChange={handleJobTypesChange}
                  />
                  <Label for="Permanent">Permanent</Label>
                </FormGroup>
                <FormGroup>
                  <Input
                    id="Contract"
                    className="me-3"
                    name="Contract"
                    type="checkbox"
                    checked={filterTemp.jobTypes.includes("Contract")}
                    onChange={handleJobTypesChange}
                  />
                  <Label for="Contract">Contract</Label>
                </FormGroup>
                <FormGroup>
                  <Input
                    id="Part-time"
                    className="me-3"
                    name="Part-time"
                    type="checkbox"
                    checked={filterTemp.jobTypes.includes("Part-time")}
                    onChange={handleJobTypesChange}
                  />
                  <Label for="Part-time">Part Time</Label>
                </FormGroup>
              </AccordionBody>
            </AccordionItem>
            <AccordionItem>
              <AccordionHeader targetId="3">LOCATION</AccordionHeader>
              <AccordionBody accordionId="3">
                {locationsToShow.map((location, index) => (
                  <FormGroup key={index}>
                    <Input
                      id={location}
                      className="me-3"
                      name={location}
                      type="checkbox"
                      checked={filterTemp.locations.includes(location)}
                      onChange={handleLocationChange}
                    />
                    <Label for={location}>{location}</Label>
                  </FormGroup>
                ))}
                {workLocations.length > 3 && (
                  <Button className="show-all-btn" onClick={() => setShowAll(!showAll)}>
                    {showAll ? "Show less" : "Show all"}
                  </Button>
                )}
              </AccordionBody>
            </AccordionItem>
          </UncontrolledAccordion>
        </Form>
      </Popover>
    </>
  );
}
